/**
 * The open claims behind the person page's "Open worklist today" counts (review #158):
 * each person's pile at the worklist snapshot, fetched only when someone opens one.
 *
 * A separate artefact (api/open-claims; scripts/build_bundle.py open_claim_detail), per
 * person in the Terminated worklists page's columnar shape. NOT Claim detail's rows:
 * those are billed claims in months the person was paid, and a pile is whatever is open
 * on their worklists, billed in any month or not at all. The build leaves out anyone
 * whose list does not add up to their counts, so a list here always ties to them.
 */
import { useCachedDetail } from './detailFetch'
import type { TerminatedClaims } from './types'
import type { AgeBucketKey } from '@/measures/core'

export interface OpenClaimsData {
  version: string
  generatedAt: string
  n: number
  people: Record<string, TerminatedClaims>
  /**
   * The piles at the latest closed month end (`Dataset.worklistMonthEnd`), aged back
   * from it (build_bundle.month_end_claim_detail, review #401). Absent on older files.
   */
  monthEnd?: { month: string; asOf: string; n: number; people: Record<string, TerminatedClaims> }
}

/** The person page's bucket keys → the claim rows' `age` values (AGE_LABEL's keys). */
export const ROW_AGE: Record<AgeBucketKey, readonly string[]> = {
  last30: ['workedLast14', 'worked15to30'],
  over30: ['workedOver30'],
  neverWorked: ['neverWorked'],
}

const OPEN_URL = (import.meta.env.VITE_OPEN_CLAIMS_URL as string | undefined) ?? '/api/open-claims'

export function useOpenClaims(
  enabled = true,
): { data: OpenClaimsData | null; error: string | null } {
  return useCachedDetail<OpenClaimsData>(
    'open-claims',
    OPEN_URL,
    'The open claims behind these counts arrive with the next data build.',
    enabled,
  )
}
